import {
  Box,
  BoxProps,
  Button,
  Center,
  createIcon,
  Flex,
  List,
  ListItem,
  ListItemProps,
  Text,
  useColorModeValue as mode,
  Tag,
  TagLabel,
  ListIcon,
  OrderedList,
  UnorderedList,
  theme,
  Grid,
  useColorModeValue,
} from "@chakra-ui/react";
import { FiUnlock } from "react-icons/fi";
import * as React from "react";
import Payment from "components/payment";
import { useSession } from "next-auth/react";
import { MdCheckCircle } from "react-icons/md";
import { SpecialEventType } from "@amplitude/types";
import { ReactNode } from "react-markdown/lib/react-markdown";
import moment from "moment";

const CheckIcon = createIcon({
  viewBox: "0 0 17 12",
  d: "M0 5.82857L1.64571 4.11429L5.48571 7.2L14.8114 0L16.4571 1.71429L5.48571 12L0 5.82857Z",
});

const PricingDetail = (props: ListItemProps) => {
  const { children, ...rest } = props;
  return (
    <ListItem
      display="flex"
      alignItems="flex-start"
      fontWeight="medium"
      // maxW="260px"
      {...rest}
    >
      <CheckIcon mr="4" mt="1" color="white" />
      <Text as="span" display="inline-block">
        {children}
      </Text>
    </ListItem>
  );
};

const PopularBadge = (props: BoxProps) => (
  <Box
    whiteSpace="nowrap"
    top="-4"
    left="50%"
    transform="translateX(-50%)"
    pos="absolute"
    rounded="md"
    fontWeight="bold"
    fontSize="sm"
    px="4"
    py="1"
    textTransform="uppercase"
    bg="blue.500"
    color="white"
    {...props}
  />
);

interface PriceDisplayProps {
  currency: string;
  price: number | string;
  billingFrequency: "yearly" | "monthly";
}

const PriceDisplay = (props: PriceDisplayProps) => {
  const { currency, price, billingFrequency } = props;
  return (
    <>
      <Flex
        w="100%"
        align="center"
        justify="center"
        mt="5"
        mb={2}
        fontWeight="extrabold"
      >
        <Text fontSize="4xl" mr="2">
          {currency}
        </Text>
        <Text fontSize="72px" lineHeight="1" letterSpacing="tight">
          {price}
        </Text>
      </Flex>
      <Text
        textAlign="center"
        fontSize="24px"
        lineHeight="1"
        letterSpacing="tight"
        mb={4}
      >
        {billingFrequency === "yearly" && (
          <Box color="gray.400" fontSize="lg">
            billed {currency}
            {Number(Number(price) * 12).toFixed(0)} annually
          </Box>
        )}
      </Text>
    </>
  );
};

export const PricingWrapper = (props: BoxProps & { highlight?: boolean }) => {
  const { highlight, ...rest } = props;

  const popularStyles: BoxProps = {
    borderWidth: "2px",
    borderColor: "blue.500",
    zIndex: 1,
    px: "8",
    pt: "12",
    pb: "10",
    top: { lg: "-2" },
  };

  const styles = highlight ? popularStyles : null;

  return (
    <Box
      w="full"
      maxW="md"
      mx="auto"
      bg={mode("white", "gray.700")}
      px="10"
      pt="8"
      pb="8"
      rounded="lg"
      // shadow="base"
      position="relative"
      {...styles}
      {...rest}
    />
  );
};

interface Feature {
  isTitle: boolean;
  icon: ReactNode;
  label: String;
  isDisabled: boolean;
  details?: string;
  isBold?: boolean;
  isDetailsBold?: boolean;
  isAi?: boolean;
}

interface PricingCardProps extends BoxProps {
  popular?: boolean;
  disableCard?: boolean;
  name: string;
  type?: string;
  plan?: string;
  discount?: string;
  customCancelUrl?: string;
  price: number;
  specialNotice: string;
  discountedFrom: string;
  description: string;
  featureIntro?: string;
  features: Array<Feature>;
  billingFrequency: "monthly" | "yearly";
  onChangeBillingFrequency: () => void;
  onClick?: () => void;
  customFeature: any;
  bottomContent?: ReactNode;
  withCounter?: boolean;
  poweredByGPT4?: boolean;
  includesChatAssist?: boolean;
  includesDatabase?: boolean;
}

function AgencyOld(props) {
  return (
    <Box>
      <Text mt={5} align="left" fontSize="32px" fontWeight="bold">
        CONTACT US
      </Text>
      <Text mt={2} align="left" fontSize="14px" fontWeight="bold">
        Need something specific like multi-account support or API access?
      </Text>
    </Box>
  );
}

function PriceAndSpecialNotice(props) {
  const color = useColorModeValue("taplio.600", "blue.200");

  const availableSpots = React.useMemo(() => {
    // array with values between 49 and 67 in random order
    // const dailyStartValuesArray = Array.from({ length: 31 }, (_, i) => i + 49).sort(() => Math.random() - 0.5)
    // console.log({ dailyStartValuesArray });
    const dailyStartValuesHardCoded = [
      72, 49, 68, 67, 76, 52, 58, 63, 74, 79, 50, 77, 70, 78, 75, 54, 66, 69,
      57, 53, 51, 73, 60, 62, 55, 71, 59, 65, 64, 56, 61,
    ];
    // console.log({ dailyStartValuesHardCoded });
    // // random value of 1 or 2
    // // const hourlyLossValue = Math.floor(Math.random() * 2) + 1
    // // array of 48 elements with values either 1 and 2
    // const hourlyLossValueArray = Array.from(
    //   { length: 48 },
    //   (_, i) => Math.floor(Math.random() * 2) + 1
    // ).sort(() => Math.random() - 0.5);

    // 48 random  values
    const hourlyLossValue = [
      0, 2, 1, 1, 0, 1, 1, 2, 1, 0, 2, 0, 1, 2, 1, 0, 1, 1, 2, 1, 1, 1, 0, 1, 0,
      0, 1, 2, 1, 0, 1, 1, 0, 2, 1, 2, 1, 1, 1, 1, 1, 0, 2, 1, 0, 2, 1, 1,
    ];

    // sum of hourlyLossValue = 46 (has to be less than available spots)
    // const hourlyLossValueSum = hourlyLossValue.reduce((a, b) => a + b, 0);
    // console.log({ hourlyLossValueSum });

    const parisTime = moment.tz("Europe/Paris");
    const dayOfMonth = parisTime.date();
    const hourOfDay = parisTime.hour();
    const halfHour = parisTime.minute() > 30 ? 1 : 0;
    const halfHourOfDay = 2 * parisTime.hour() + halfHour;

    // console.log({ dayOfMonth, hourOfDay, halfHourOfDay });
    const dailySpots = dailyStartValuesHardCoded[dayOfMonth - 1];
    // console.log({ dailySpots });
    // sum of loss values for the day
    const dailyLossValue = hourlyLossValue
      .slice(0, halfHourOfDay)
      .reduce((a, b) => a + b, 0);
    // console.log({ dailyLossValue });
    const availableSpots = dailySpots - dailyLossValue;
    // console.log({ availableSpots });
    return availableSpots;
  }, []);

  return (
    <Flex
      alignItems="flex-start"
      flexDirection={"column"}
      {...{
        color,
        fontWeight: 700,
        mt: 4,
      }}
    >
      {props.discountedFrom != "" && (
        <Text
          textDecoration="line-through"
          fontSize="24px"
          fontWeight="700"
          {...{ mb: -2 }}
        >
          {"$" + props.discountedFrom}
        </Text>
      )}
      <Flex {...{ alignItems: "center" }}>
        <Text align="left" fontSize="38px" fontWeight="bold">
          ${props.price}
        </Text>
        <Text fontSize="30px" {...{ ml: 1.5, mr: 0.5 }}>
          /
        </Text>
        <Text
          fontSize="30px"
          // {...{ ml: 1.5, mr: 1 }}
        >
          mo
        </Text>
      </Flex>
      {props.billingFrequency === "yearly" && (
        <Text {...{ fontSize: "16px", fontStyle: "italic", fontWeight: "500" }}>
          {`Billed $${props.price * 12}/year`}
        </Text>
      )}
      {/* {props.specialNotice && ( */}
      {props.withCounter && (
        <Box
          {...{
            // w:"full",
            bg: "white",
            px: 3,
            py: 2,
            mt: 2,
            fontSize: "11px",
            borderRadius: "lg",
          }}
        >
          {/* {props.specialNotice} */}
          {`For the next ${availableSpots} people to sign up`}
        </Box>
      )}
    </Flex>
  );
}

function AgencyLetsTalk() {
  return (
    <Button
      colorScheme="gray"
      variant="outline"
      size="lg"
      w="200px"
      as="a"
      target="_blank"
      href="mailto:team@taplio.com"
    >
      LET'S TALK
    </Button>
  );
}

function PopularPlan() {
  return (
    <Flex // position="absolute"
      // right="20px"
      // top="20px"
      w="100%"
      justifyContent="center"
      mb={2}
    >
      <Tag
        colorScheme="whiteAlpha"
        color={mode("gray.200", "white")}
        borderRadius="lg"
        size="lg"
        variant="outline"
      >
        <TagLabel fontSize="sm" fontWeight="400">
          82% of people pick this plan
        </TagLabel>
      </Tag>
    </Flex>
  );
}

function PlanName(props) {
  const color = useColorModeValue("taplio.600", "blue.200");
  return (
    <Text
      align="left"
      // mt={4}
      // mb={-2}
      // mx="auto"
      fontSize="24px"
      fontWeight="700"
      {...{ color, fontFamily: "Poppins" }}
    >
      {props.name}
    </Text>
  );
}

function PlanDescription(props) {
  const color = useColorModeValue(undefined, "taplio.100");
  return (
    <Text
      align="left"
      mt={2}
      mb={0}
      // mx="auto"
      fontSize="xs"
      fontWeight="500"
      {...{
        color,
        fontFamily: "Poppins",
      }}
    >
      {props.description}
    </Text>
  );
}

function TopComponent(props) {
  const color = useColorModeValue("white", "blue.800");
  return (
    <Grid
      {...{
        bg: "#FAD459",
        borderRadius: "lg",
        px: 2,
        py: 0,
        h: 8,
        position: "relative",
        mb: "-6",
        mx: 12,
        zIndex: 9,
        placeContent: "center",
        fontWeight: 800,
        fontSize: "xs",
        fontFamily: "Poppins",
        color,
      }}
    >
      <Text>{props.top as string}</Text>
    </Grid>
  );
}

function CardWrapper({ children, popular }) {
  const cardBackground = useColorModeValue("white", "gray.800");
  const cardBackgroundPopular = useColorModeValue("#DFEDFF", "gray.700");
  const borderColor = useColorModeValue(
    "#B9B9B9",
    popular ? "gray.600" : "gray.700"
  );
  return (
    <Flex
      flexDir="column"
      justifyContent="center"
      alignItems="center"
      {...{
        border: "1px solid",
        borderColor,
        borderRadius: "lg",
        mt: popular ? "0" : "10",
        shadow: popular ? "lg" : "none",
        overflow: "hidden",
        fontFamily: "Poppins",
        bg: popular ? cardBackgroundPopular : cardBackground,
        // mt: popular ? "-4" : "0",
      }}
    >
      <Box
        {...{
          bg: popular ? "rgba(0, 112, 255, 0.5)" : "rgba(0, 112, 255, 0.2)",
          h: 4,
          w: "full",
        }}
      ></Box>
      <Flex
        {...{
          flexDirection: "column",
          p: 6,
          px: [4, 6],
        }}
      >
        {children}
      </Flex>
    </Flex>
  );
}

function FeatureIcon(props) {
  const color = useColorModeValue("#777", "taplio.50");
  return (
    <Grid
      {...{
        placeSelf: "start",
        pt: props.isTitle ? "8px" : "8px",
        w: 4,
        mt: props.isTitle ? "14px" : 0,
        // mb: props.isTitle ? 2 : 0,
        // fontSize: "lg",
        textAlign: "left",
        placeItems: "end",
        color,
      }}
    >
      {(!props.isDisabled || props.isTitle) && props.icon}
    </Grid>
  );
}

function Features({ features }) {
  return (
    <Flex
      {...{
        flexDirection: "column",
        mt: 4,
      }}
    >
      {features?.map(
        (
          {
            isTitle,
            isDisabled,
            label,
            icon,
            details,
            isBold,
            isDetailsBold,
            isAi,
          },
          mapIndex
        ) => {
          // const labelColor = isTitle ? "taplio.500" : "#777" useColorModeValue("gray.700", "gray.200");
          const labelColorTitle = useColorModeValue("taplio.500", "blue.300");
          const labelColorTitleDisabled = useColorModeValue(
            "#5A5A5A",
            "taplio.100"
          );
          const labelColor = useColorModeValue("#5A5A5A", "taplio.100");
          const detailsColor = useColorModeValue("taplio.500", "blue.400");
          return (
            <Grid
              key={mapIndex}
              {...{
                gridTemplateColumns: "16px 1fr",
                gap: 1.5,
                placeContent: "center",
              }}
            >
              <FeatureIcon
                isTitle={isTitle}
                isDisabled={isDisabled}
                icon={icon}
              ></FeatureIcon>
              <Grid
                {...{
                  fontWeight: isTitle
                    ? 700
                    : isBold
                    ? 700
                    : isDisabled
                    ? 300
                    : 600,
                  fontSize: isTitle ? "sm" : "xs",
                  color:
                    // isDisabled ? "#A00000" :
                    // isBold ? "#1E8728" :
                    isTitle
                      ? isDisabled
                        ? labelColorTitleDisabled
                        : labelColorTitle
                      : labelColor,
                  mt: isTitle ? 6 : 1,
                  mb: isTitle ? 2 : 1,
                  ml: isDisabled && !isTitle ? "-20px" : "0",
                  placeContent: "center start",
                  textDecoration: isDisabled ? "line-through" : "",
                  textDecorationThickness: ".5px",
                  bgGradient: isAi
                    ? "linear-gradient(90.66deg, #CD6C8E 0.37%, #7B6BCA 73.77%)"
                    : "",
                  bgClip: isAi ? "text" : "",
                }}
              >
                {label}
                <Text
                  {...{
                    mt: 1,
                    fontSize: "xs",
                    fontWeight: isDetailsBold ? 700 : 600,
                    color:
                      // isDetailsBold ? "#1E8728" :
                      detailsColor,
                  }}
                >
                  {details}
                </Text>
              </Grid>
            </Grid>
          );
        }
      )}
    </Flex>
  );
}

function ThirtyDayMoneyBack() {
  const color = useColorModeValue("#5B5B5B", "taplio.100");
  return (
    <Text
      {...{
        mt: 2,
        textAlign: "center",
        color,
        fontSize: "xs",
        fontWeight: 400,
      }}
    >
      +30-day money-back guarantee
    </Text>
  );
}

export const PricingCard = (props: PricingCardProps) => {
  const {
    name,
    plan = "start",
    description,
    top,
    type = "normal",
    onClick,
    discount,
    customFeature,
    price,
    specialNotice,
    popular,
    billingFrequency,
    features,
    discountedFrom = "",
    featureIntro,
    customCancelUrl = undefined,
    disableCard = false,
    onChangeBillingFrequency,
    //@ts-ignore
    onClickStartFreeTrial = undefined,
    withCounter = false,
    poweredByGPT4 = false,
    includesChatAssist = false,
    includesDatabase = false,
    ...rest
  } = props;

  const { data: session } = useSession() ?? {};

  const poweredByGPT4Label = "Powered by GPT-4";
  return (
    <>
      {!!top && <TopComponent top={top}></TopComponent>}
      <CardWrapper {...{ popular }}>
        {/* <PricingWrapper mb={5} highlight={popular} {...rest} bg={bg} maxW="300px" p={5}> */}
        {!disableCard && (
          <Box>
            {/* <PricingWrapper {...rest} mb={5} maxW="325px" minW="300px" p={5}> */}
            {/* {popular && <PopularPlan></PopularPlan>} */}
            <Flex
              {...{
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <PlanName name={name}></PlanName>
              {poweredByGPT4 && (
                <Grid {...{ gridTemplate: `"stack"` }}>
                  <Grid
                    {...{
                      gridArea: "stack",
                      bgGradient:
                        "linear-gradient(90.66deg, #CD6C8E 0.37%, #7B6BCA 73.77%)",
                      borderRadius: "7px",
                    }}
                  ></Grid>
                  <Grid
                    {...{
                      gridArea: "stack",
                      bg: popular ? "#DFEDFF" : "#fff",
                      m: "1.5px",
                      borderRadius: "5.5px",
                    }}
                  ></Grid>
                  <Grid
                    {...{
                      gridArea: "stack",
                      gridTemplate: `"a b"`,
                      placeItems: "center",
                      gap: 0.5,
                      px: 2.5,
                      py: 1,
                    }}
                  >
                    <Grid
                      {...{
                        fontWeight: 700,
                        fontSize: "8px",
                        bgClip: "text",
                        bgGradient:
                          "linear-gradient(90.66deg, #CD6C8E 0.37%, #7B6BCA 73.77%)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {poweredByGPT4Label}
                    </Grid>
                    <Box {...{ fontSize: "9px" }}>⚡️</Box>
                  </Grid>
                </Grid>
              )}
            </Flex>

            <PlanDescription description={description}></PlanDescription>
            {type == "agencyOld" ? (
              <AgencyOld></AgencyOld>
            ) : (
              <PriceAndSpecialNotice
                discountedFrom={discountedFrom}
                price={price}
                specialNotice={specialNotice}
                // color={color}
                billingFrequency={billingFrequency}
                withCounter={withCounter}
              ></PriceAndSpecialNotice>
            )}

            {/* </PricingWrapper> */}
            {type == "agency" ? (
              <AgencyLetsTalk></AgencyLetsTalk>
            ) : (
              <>
                <Box {...{ mt: 4 }}>
                  <Payment
                    compact={"true"}
                    discount={discount}
                    variant={popular ? "taplioPayment" : "taplioPaymentOutline"} // button variant
                    plan={
                      plan +
                      (billingFrequency === "yearly" ? "Yearly" : "Monthly")
                    }
                    billingFrequency={billingFrequency}
                    session={session}
                    customCancelUrl={
                      customCancelUrl ? customCancelUrl : "subscribe"
                    }
                    onClickStartFreeTrial={onClickStartFreeTrial}
                    source="subscribe"
                  />
                </Box>
                <ThirtyDayMoneyBack></ThirtyDayMoneyBack>
              </>
            )}
          </Box>
        )}
        {includesChatAssist && (
          <Grid {...{ gridTemplate: `"stack"`, mt: 5, mb: -2 }}>
            <Grid
              {...{
                gridArea: "stack",
                bgGradient:
                  "linear-gradient(90.66deg, #CD6C8E 0.37%, #7B6BCA 73.77%)",
                borderRadius: "7px",
              }}
            ></Grid>
            <Grid
              {...{
                gridArea: "stack",
                bg: "#fff",
                m: "2px",
                borderRadius: "5px",
              }}
            ></Grid>
            <Grid
              {...{
                gridArea: "stack",
                gridTemplate: `"a" "b"`,
                placeItems: "center",
                gap: 0.5,
                px: 1,
                py: 4,
                fontWeight: 600,
                fontSize: "12.5px",
                bgClip: "text",
                bgGradient:
                  "linear-gradient(90.66deg, #CD6C8E 0.37%, #7B6BCA 73.77%)",
                // whiteSpace: "nowrap",
              }}
            >
              <Flex {...{ gap: 0.5 }}>
                <Box as="span">Includes</Box>
                <Box as="span" {...{ fontWeight: 800 }}>
                  Chat Assist
                </Box>
              </Flex>
              <Box
                as="span"
                {...{ fontSize: "9.5px", gap: 0.5, textAlign: "center" }}
                px={2}
              >
                <Box as="span" whiteSpace={"nowrap"}>
                  Your Growth assistant,
                </Box>{" "}
                <Box as="span" whiteSpace={"nowrap"}>
                  powered by GPT-4
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
        {includesDatabase && (
          <Grid {...{ gridTemplate: `"stack"`, mt: 5, mb: -2 }}>
            <Grid
              {...{
                gridArea: "stack",
                bgGradient:
                  "linear-gradient(90.66deg, #3376ED 0.37%, #6AA7C9 73.77%)",
                borderRadius: "7px",
              }}
            ></Grid>
            <Grid
              {...{
                gridArea: "stack",
                bg: "#fff",
                m: "2px",
                borderRadius: "5px",
              }}
            ></Grid>
            <Grid
              {...{
                gridArea: "stack",
                gridTemplate: `"a" "b"`,
                placeItems: "center",
                gap: 0.5,
                px: 1,
                py: 4,
                fontWeight: 600,
                fontSize: "12.5px",
                bgClip: "text",
                // bgGradient: "linear-gradient(90.66deg, #CD6C8E 0.37%, #7B6BCA 73.77%)",
                bgGradient:
                  "linear-gradient(90.66deg, #3376ED 0.37%, #6AA7C9 73.77%)",
              }}
            >
              <Flex {...{ gap: 0.5 }}>
                <Box as="span">Includes</Box>
                <Box as="span" {...{ fontWeight: 800 }}>
                  Database
                </Box>
              </Flex>
              <Box
                as="span"
                {...{ fontSize: "9.5px", gap: 0.5, textAlign: "center" }}
                px={2}
              >
                {/* <Box as="span" whiteSpace={"nowrap"}>Find leads within millions of LinkedIn accounts.</Box> */}
                <Box as="span" whiteSpace={"nowrap"}>
                  Millions of searchable AI-enriched leads
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
        {features && <Features features={features}></Features>}
        {props.bottomContent}
      </CardWrapper>
    </>
  );

  // return (
  //   <Flex flexDir="column" justifyContent="center" alignItems="center">
  //     {/* <PricingWrapper mb={5} highlight={popular} {...rest} bg={bg} maxW="300px" p={5}> */}
  //     {!disableCard && (
  //       <Box>
  //         <PricingWrapper
  //           mb={5}
  //           {...rest}
  //           bg={bg}
  //           maxW="325px"
  //           minW="300px"
  //           p={5}
  //         >
  //           {popular && (
  //             <Flex
  //               // position="absolute"
  //               // right="20px"
  //               // top="20px"
  //               w="100%"
  //               justifyContent="center"
  //               mb={2}
  //             >
  //               <Tag
  //                 colorScheme="whiteAlpha"
  //                 color={mode("gray.200", "white")}
  //                 borderRadius="lg"
  //                 size="lg"
  //                 variant="outline"
  //               >
  //                 <TagLabel fontSize="sm" fontWeight="400">
  //                   82% of people pick this plan
  //                 </TagLabel>
  //               </Tag>
  //             </Flex>
  //           )}
  //           <Text
  //             align="left"
  //             mt={0}
  //             mb={-2}
  //             color={color}
  //             // maxW="16rem"
  //             mx="auto"
  //             fontSize="22px"
  //             fontWeight="700"
  //           >
  //             {name}
  //           </Text>
  //           {/* {
  //               popular && (
  //                 <Box
  //                   position="absolute"
  //                   right="20px"
  //                   top="20px"
  //                 >
  //                   <Tag bg="yellow.300" colorScheme="yellow" borderRadius="full" size="sm">
  //                     <TagLabel fontSize="xs" fontWeight="800" color="#1E1E1E">
  //                       Recommended
  //                     </TagLabel>
  //                   </Tag>
  //                 </Box>
  //               )
  //             } */}
  //           {type == "agencyOld" ? (
  //             <Box>
  //               <Text
  //                 mt={5}
  //                 align="left"
  //                 fontSize="32px"
  //                 fontWeight="bold"
  //                 color={color}
  //               >
  //                 CONTACT US
  //               </Text>
  //               <Text
  //                 mt={2}
  //                 align="left"
  //                 fontSize="14px"
  //                 fontWeight="bold"
  //                 color={color}
  //               >
  //                 Need something specific like multi-account support or API
  //                 access?
  //               </Text>
  //             </Box>
  //           ) : (
  //             <>
  //               <Flex alignItems="center">
  //                 {discountedFrom != "" && (
  //                   <Box h="50px">
  //                     <Text
  //                       textDecoration="line-through"
  //                       mr={3}
  //                       mt={0}
  //                       maxW="60px"
  //                       align="left"
  //                       fontSize="20px"
  //                       fontWeight="400"
  //                       color={color}
  //                     >
  //                       {/* <Text bg="linear-gradient(to left top, transparent 47.75%, currentColor 49.5%, currentColor 50.5%, transparent 52.25%)" mr={3} mt={0} maxW="60px" align="left" fontSize="20px" fontWeight="400" color={color}> */}
  //                       {"$" + discountedFrom}
  //                     </Text>
  //                   </Box>
  //                 )}
  //                 <Text
  //                   align="left"
  //                   fontSize="50px"
  //                   fontWeight="bold"
  //                   color={color}
  //                 >
  //                   ${price} /
  //                 </Text>
  //                 <Text
  //                   ml={4}
  //                   mt={1}
  //                   maxW="60px"
  //                   align="left"
  //                   fontSize="16px"
  //                   fontWeight="bold"
  //                   color={color}
  //                 >
  //                   per month
  //                 </Text>
  //               </Flex>
  //               {billingFrequency == "yearly" && (
  //                 <Text opacity="0.6" color={color} fontSize="sm">
  //                   ${price * 12} billed annually
  //                 </Text>
  //               )}
  //               <Flex mt={2} direction="column">
  //                 {specialNotice && (
  //                   <Text
  //                     align="left"
  //                     mt={0}
  //                     mb={2}
  //                     color={color}
  //                     // maxW="16rem"
  //                     // mx="auto"
  //                     fontSize="md"
  //                     fontWeight="300"
  //                     as="i"
  //                   >
  //                     {specialNotice}
  //                   </Text>
  //                 )}
  //                 <Text
  //                   align="left"
  //                   mt={0}
  //                   mb={0}
  //                   color={color}
  //                   // maxW="16rem"
  //                   // mx="auto"
  //                   fontSize="md"
  //                   fontWeight="500"
  //                 >
  //                   {description}
  //                 </Text>
  //               </Flex>
  //             </>
  //           )}
  //         </PricingWrapper>
  //         {type == "agency" ? (
  //           <Button
  //             // bg={bg}
  //             // color={color}
  //             colorScheme="gray"
  //             variant="outline"
  //             // bg="white"
  //             // border={"1px solid " + theme.colors.gray[400]}
  //             size="lg"
  //             w="200px"
  //             as="a"
  //             target="_blank"
  //             href="mailto:team@taplio.com"
  //           >
  //             LET'S TALK
  //           </Button>
  //         ) : (
  //           <Payment
  //             discount={discount}
  //             color={mode("blackAlpha", "whiteAlpha")}
  //             bg={bg}
  //             textColor={color}
  //             plan={
  //               plan + (billingFrequency === "yearly" ? "Yearly" : "Monthly")
  //             }
  //             billingFrequency={billingFrequency}
  //             session={session}
  //             customCancelUrl={
  //               customCancelUrl ? customCancelUrl : "onboardnewchoice"
  //             }
  //             onClickStartFreeTrial={onClickStartFreeTrial}
  //             source="onboardNewChoice"
  //             border={"1px solid " + theme.colors.gray[400]}
  //           />
  //         )}
  //       </Box>
  //     )}
  //     {features !== undefined && (
  //       <List mt={10} spacing={5}>
  //         {customFeature ? (
  //           <ListItem mt={0} fontWeight="700">
  //             {customFeature}
  //           </ListItem>
  //         ) : (
  //           <ListItem mt={0} fontWeight="700">
  //             {featureIntro}
  //           </ListItem>
  //         )}
  //         {features.map((e, index) => {
  //           return (
  //             <ListItem key={index} mt={0} display="flex" alignItems="center">
  //               <ListIcon
  //                 as={MdCheckCircle}
  //                 color={mode("gray.600", "gray.100")}
  //                 fontSize="20px"
  //               />
  //               <Flex flexDirection="column">
  //                 <Text fontWeight="500">{e.title}</Text>
  //                 {e.desc && (
  //                   <Text mt={4} fontWeight="400" fontSize="sm">
  //                     {e.desc}
  //                   </Text>
  //                 )}
  //               </Flex>
  //             </ListItem>
  //           );
  //         })}
  //       </List>
  //     )}
  //   </Flex>
  // );
};
